import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileCheck, FileWarning, FileSearch, Repeat, Shield, MessageCircle, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import PageHero from '../components/PageHero';
import GuaranteeCard from '../components/GuaranteeCard';
import Reviews from '../components/Reviews';
import FAQ from '../components/FAQ';
import { ContactSection, Footer } from '../components/Footer';

const services = [
  { key: 'GST Registration', Icon: FileCheck, title: 'GST Registration', desc: 'Fresh GSTINs across all states with 96% first-attempt approval.', price: 'From ₹1,999' },
  { key: 'Monthly GST Filing', Icon: Repeat, title: 'Monthly GST Filing', desc: 'GSTR-1, GSTR-3B, GSTR-9 filed on time — every month, every state.', price: 'From ₹499/mo' },
  { key: 'Show Cause Notice', Icon: FileWarning, title: 'Show Cause Notice Reply', desc: 'Draft, submit and represent — full response to any GST notice.', price: 'From ₹2,500' },
  { key: 'GST Revocation', Icon: FileSearch, title: 'GST Revocation', desc: 'Revive cancelled or suspended GSTINs with department representation.', price: 'From ₹4,999' },
  { key: 'GST Refund', Icon: Shield, title: 'GST Refund', desc: 'Claim ITC refunds, export refunds, inverted duty structure refunds.', price: 'From ₹4,999' },
  { key: 'GST Appeal', Icon: Shield, title: 'GST Appeal', desc: 'Appeal against adverse orders — First Appellate Authority to Tribunal.', price: 'From ₹14,999' },
  { key: 'Summons Or Departmental Cases', Icon: FileWarning, title: 'Summons / Departmental Cases', desc: 'On-ground representation for summons, departmental audits, investigations.', price: 'From ₹9,999' },
  { key: 'Advisory & Amendments', Icon: MessageCircle, title: 'Advisory & Amendments', desc: 'Core / non-core field changes, authorised signatory updates.', price: 'From ₹1,500' },
];

export default function GstAdvisoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preSubtype = searchParams.get('service') || 'General';

  useEffect(() => {
    document.title = 'GST Registration, Filing & Advisory | EasyWorks';
    window.scrollTo(0, 0);
  }, []);

  // When URL arrives with ?service=... (e.g. from footer links), auto-scroll to the form.
  useEffect(() => {
    const svc = searchParams.get('service');
    if (!svc) return;
    const t = setTimeout(() => {
      const form = document.querySelector('[data-testid="gst-quote-form"]');
      if (form) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(t);
  }, [searchParams]);

  const pickService = (key) => {
    setSearchParams({ service: key });
    // Smooth-scroll to the form and briefly flash it so the user can see it moved
    setTimeout(() => {
      const form = document.querySelector('[data-testid="gst-quote-form"]');
      if (form) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid="gst-advisory-page">
      <Navbar />
      <PageHero
        key={preSubtype}
        eyebrow="GST Advisory"
        headline="Zero notices. Zero late fees."
        headlineAccent="Just clean GST."
        description="End-to-end GST for e-commerce sellers, D2C brands and MSMEs. Registration, monthly filings, notices, audits, refunds — one team, one dashboard."
        category="gst"
        subtype={preSubtype}
        service={preSubtype === 'General' ? 'GST Advisory' : preSubtype}
        formTitle={preSubtype === 'General' ? 'Talk to a GST expert' : `Enquire about ${preSubtype}`}
        formSubtitle="Custom pricing and timeline within 24 hours."
        formTestId="gst-quote-form"
      />
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">GST services under one roof</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map(({ Icon, title, desc, price, key }) => (
              <button
                key={title}
                type="button"
                onClick={() => pickService(key)}
                data-testid={`gst-service-tile-${key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="text-left bg-[#FAF2E8]/60 border border-black/5 rounded-2xl p-6 hover:border-[#F39200] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm text-[#F39200] flex items-center justify-center">
                  <Icon size={22} />
                </div>
                <div className="mt-4 text-lg font-semibold text-neutral-900">{title}</div>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex-1">{desc}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm font-bold text-[#F39200]">{price}</div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#F39200] group-hover:gap-2 transition-all">Get quote <ArrowRight size={12} /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      <GuaranteeCard />
      <Reviews />
      <FAQ />
      <ContactSection />
      <Footer />
    </div>
  );
}
